import { QuestionIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { DropdownFilter, type DropdownElement } from '../../components/dropdown-filter.tsx'
import { Checkbox } from '../../components/input/checkbox.tsx'
import { SearchInput } from '../../components/input/search-input.tsx'
import { QuickViewSelector } from '../../components/menus/quick-view-selector.tsx'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { UNGROUPED_PROJECT_FILTER, useGetProjectsPage } from '../../utils/api-client/api-hooks.ts'
import * as styles from './repositories-controls.css.ts'
import { repositoryQuickViewItems, type RepositoryQuickViewId } from './repository-quick-views.ts'
import { RISK_BANDS, riskBandLabel, type RiskBandId } from './repository-risk.ts'

type RepositoriesControlsProps = {
  /** Omitted while the count is still loading, so the heading does not flash a zero. */
  count?: number
  quickView: RepositoryQuickViewId
  onChangeQuickView: (view: RepositoryQuickViewId) => unknown
  /**
   * Whether the estate has enough scored repositories to rank. Below the threshold the ranked view is not
   * offered and the filter has no Risk score group.
   */
  isRiskAvailable: boolean
  selectedBands: RiskBandId[]
  onToggleBand: (band: RiskBandId) => unknown
  onResetBands: () => unknown
  /** Project ids, or {@link UNGROUPED_PROJECT_FILTER}, the list is narrowed to. */
  selectedProjectIds: string[]
  onToggleProject: (projectId: string) => unknown
  onResetProjects: () => unknown
  /** Ranked view only: off by default, so the ranking is a ranking of scored repositories. */
  isShowingUnranked: boolean
  onToggleShowUnranked: (isShowing: boolean) => unknown
  searchTerm: string
  onChangeSearchTerm: (value: string) => unknown
  onOpenAddRepository: () => unknown
  description: string
  /** Set while the list is narrowed to one integration instance, from a `?integration-id=` link. */
  integrationFilterId?: string
  /** The filtered instance's name, once known. Without it the notice says "one integration". */
  integrationFilterName?: string
  onClearIntegrationFilter: () => unknown
}

/**
 * Says which "project" the filter group means. Source control and scanning tools each have a "project" of
 * their own, and none of them is this one.
 */
const ProjectDisambiguation: React.FC = () => (
  <TooltipWithHover
    size="small"
    trigger={
      <span aria-label="About Pixee projects" role="img" className={styles.projectHelpIcon}>
        <QuestionIcon size={14} weight="bold" />
      </span>
    }
  >
    A Pixee project groups repositories to scope feedback. It is not a project in your source control or scanning tools.
  </TooltipWithHover>
)

/**
 * How many projects the group offers before anything is typed — the size the repository pickers' searches
 * use: enough to browse in a scrolling panel, with the search box reaching everything past it.
 */
const PROJECT_OPTIONS_PAGE_SIZE = 50

/**
 * The repositories page's header and action bar.
 *
 * Follows the findings table's controls: the quick view selector names what you are looking at on the
 * left, and search plus the filter dropdown sit on the right. It replaced a bespoke two-option switch —
 * the quick view carries an ordering and a filter state with it, which a toggle cannot.
 */
export const RepositoriesControls: React.FC<RepositoriesControlsProps> = ({
  count,
  quickView,
  onChangeQuickView,
  isRiskAvailable,
  selectedBands,
  onToggleBand,
  onResetBands,
  selectedProjectIds,
  onToggleProject,
  onResetProjects,
  isShowingUnranked,
  onToggleShowUnranked,
  searchTerm,
  onChangeSearchTerm,
  onOpenAddRepository,
  description,
  integrationFilterId,
  integrationFilterName,
  onClearIntegrationFilter,
}) => {
  // An organization can have hundreds of projects, so the group offers one page of them and its search box
  // is the API's name filter, reaching the rest without reading every page. Once a search has been typed
  // the group stays mounted even when nothing matches, so a query that finds nothing leaves the box to
  // clear rather than taking it away.
  const [projectSearch, setProjectSearch] = useState('')
  const { data: projectsPage } = useGetProjectsPage({ name: projectSearch.trim(), size: PROJECT_OPTIONS_PAGE_SIZE })
  const projects = projectsPage?._embedded.items ?? []
  const hasProjectSelection = selectedProjectIds.length > 0
  // A selection keeps the group on screen even when the search, or the organization, has nothing to
  // show: a `?project=` for a project that has since been deleted is otherwise only clearable by
  // editing the URL, because the Filter button it would be cleared from never renders.
  const hasProjects = (projectsPage?.total ?? 0) > 0 || projectSearch.trim() !== '' || hasProjectSelection

  const projectGroup: DropdownElement | undefined = hasProjects
    ? {
        type: 'group',
        id: 'project',
        // Always "Pixee project". Azure carries its own project, the settings drawer labels a field
        // "Project" for every provider, GitLab calls its repositories projects, and Sonar, Checkmarx and
        // Black Duck each have one of their own. Switching on the page in front of the reader made the
        // label flicker as they paged, and the signal it would need — whether the organization holds an
        // Azure repository at all — is not on the page.
        text: 'Pixee project',
        help: <ProjectDisambiguation />,
        // Hundreds of projects do not fit a scrollable panel, so the group filters its own options.
        searchLabel: 'Search projects',
        onSearchChange: setProjectSearch,
        searchValue: projectSearch,
        onToggleGroup: onResetProjects,
        // From the URL rather than the options: a selected project outside the page on offer still
        // filters the table, so the group has to read as selected and clear it from its checkbox.
        hasSelection: hasProjectSelection,
        filters: [
          ...projects.map(project => ({
            type: 'checkbox' as const,
            id: `project-${project.id}`,
            text: project.name,
            value: selectedProjectIds.includes(project.id),
            onToggle: () => onToggleProject(project.id),
          })),
          {
            type: 'checkbox' as const,
            id: 'project-ungrouped',
            text: 'Ungrouped',
            value: selectedProjectIds.includes(UNGROUPED_PROJECT_FILTER),
            onToggle: () => onToggleProject(UNGROUPED_PROJECT_FILTER),
          },
        ],
      }
    : undefined

  const bandGroup: DropdownElement | undefined = isRiskAvailable
    ? {
        type: 'group',
        id: 'band',
        text: 'Risk score',
        onToggleGroup: onResetBands,
        // The range alone, with no Critical/High/… names: those belong to a finding's severity, which is a
        // different scale measured differently, and sharing the words would imply the two convert.
        filters: RISK_BANDS.map(band => ({
          type: 'checkbox' as const,
          id: `band-${band.id}`,
          text: riskBandLabel(band),
          value: selectedBands.includes(band.id),
          onToggle: () => onToggleBand(band.id),
        })),
      }
    : undefined

  const filterElements = [projectGroup, bandGroup].filter(group => !!group)
  const resetAllFilters = () => {
    onResetProjects()
    onResetBands()
  }

  return (
    <div className={styles.pageHead}>
      <div className={styles.header}>
        <h4 className={styles.heading}>Repositories</h4>
        <p className={styles.description}>{description}</p>
        {integrationFilterId && (
          <div className={styles.integrationFilterNotice} role="status">
            <span>Showing repositories that use {integrationFilterName ?? 'one integration'}.</span>
            <button type="button" className={styles.integrationFilterClear} onClick={onClearIntegrationFilter}>
              Show all repositories
            </button>
          </div>
        )}
      </div>

      <div className={styles.controls}>
        <div className={styles.left}>
          <QuickViewSelector<RepositoryQuickViewId>
            quickViews={repositoryQuickViewItems(isRiskAvailable)}
            selectedQuickView={quickView}
            onSelectedQuickViewChange={onChangeQuickView}
            currentCount={count}
          />

          {/* Beside the view it qualifies, not among the search and filter controls: it says what this
              ranking leaves out. Ranked view only — in the unordered list nothing is ranked, so it would
              toggle nothing. Unchecked by default, and the checkbox is what tells a reader unscored
              repositories are being left out. */}
          {quickView === 'ranked-by-risk' && (
            <div className={styles.rankingQualifiers}>
              <label className={styles.showUnranked}>
                <Checkbox
                  id="show-unranked"
                  checked={isShowingUnranked}
                  onCheckedChange={onToggleShowUnranked}
                  ariaLabel="Show unranked"
                />
                Show unranked
              </label>

              {/* The ranking orders the rows already fetched, not the estate: the list is paginated
                  server-side, so ordering has to happen before pagination to rank across pages. Said
                  here rather than left to be inferred, because page one otherwise reads as the estate's
                  riskiest. Comes out when the API can order. */}
              <span className={styles.rankingScope}>Ranked per page</span>
            </div>
          )}
        </div>

        <div className={styles.right}>
          <div className={styles.searchSlot}>
            <SearchInput
              ariaLabel="Search repositories"
              value={searchTerm}
              onChange={onChangeSearchTerm}
              debounce={300}
            />
          </div>

          {/* Shown once it has a group to open onto: the project group needs a project, a selection or a
              typed search, and the risk-score group needs enough scored repositories to rank. */}
          {filterElements.length > 0 && (
            <DropdownFilter
              filterElements={filterElements}
              onToggleAllFiltersOff={resetAllFilters}
              isAnyNonDefaultFilterActive={selectedBands.length > 0 || hasProjectSelection}
            />
          )}
          <DefaultButton size="small" onClick={onOpenAddRepository}>
            + &nbsp; Connect new
          </DefaultButton>
        </div>
      </div>
    </div>
  )
}
